import { assertUnreachable, entries, fromEntries, search, sortBy, stringCompare, } from '@debbie/common/dist/util/index.js';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../theme.js';
import { ArrowLeft, ArrowRight } from './Icons/index.js';
import * as filters from './power-table/Filters.js';
import View from './power-table/View.js';
export var FilterType;
(function (FilterType) {
    FilterType["DateRange"] = "DATE_RANGE";
    FilterType["NumberRange"] = "NUMBER_RANGE";
    FilterType["MoneyRange"] = "MONEY_RANGE";
    FilterType["Search"] = "SEARCH";
    FilterType["Category"] = "CATEGORY";
    FilterType["Boolean"] = "BOOLEAN";
})(FilterType || (FilterType = {}));
const PowerTable = (props) => {
    const lang = useSelector((s) => s.lang);
    const [sortKey, setSortKey] = useState(props.defaultSortKey);
    const [direction, setDirection] = useState(props.defaultSortDirection ?? 'ASC');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [appliedFilters, setAppliedFilters] = useState(entries(props.values).reduce((acc, [key, val]) => {
        acc[key] = undefined;
        if (val?.filter?.type === FilterType.Category) {
            acc[key] = {
                type: FilterType.Category,
                categories: [],
            };
        }
        if (val?.filter?.type === FilterType.Search) {
            acc[key] = {
                type: FilterType.Search,
                query: null,
            };
        }
        if (val?.filter?.type === FilterType.DateRange) {
            acc[key] = {
                type: FilterType.DateRange,
                from: null,
                to: null,
            };
        }
        if (val?.filter?.type === FilterType.NumberRange) {
            acc[key] = {
                type: FilterType.NumberRange,
                from: null,
                to: null,
            };
        }
        if (val?.filter?.type === FilterType.MoneyRange) {
            acc[key] = {
                type: FilterType.MoneyRange,
                from: null,
                to: null,
            };
        }
        if (val?.filter?.type === FilterType.Boolean) {
            acc[key] = {
                type: FilterType.Boolean,
                showTrue: undefined,
            };
        }
        return acc;
    }, {}));
    const list = useMemo(() => {
        let sortFn = null;
        if (sortKey == null) {
            sortFn = null;
        }
        else {
            const val = props.values[sortKey];
            if (!val || val.sortable === false) {
                sortFn = null;
            }
            else {
                if (val.toCmp) {
                    const fn = val.toCmp;
                    sortFn = ((item) => fn(item[sortKey]));
                }
                else {
                    sortFn = ((item) => item[sortKey]);
                }
            }
        }
        return sortFn != null
            ? sortBy(props.data, sortFn, {
                reverse: direction === 'DESC' ? true : false,
            })
            : props.data;
    }, [props.data, sortKey, direction]);
    const filteredList = useMemo(() => {
        const filterList = entries(appliedFilters);
        return list.filter((item) => {
            const invalid = filterList.find(([key, filter]) => {
                // Return true if not valid
                if (filter == null) {
                    return false;
                }
                switch (filter?.type) {
                    case FilterType.DateRange: {
                        let val = item[key];
                        const filterOption = props.values[key].filter;
                        if (filterOption.type === FilterType.DateRange &&
                            filterOption.toDate) {
                            val = filterOption.toDate(item[key]);
                        }
                        if (filter.from) {
                            if (filter.from > val) {
                                return true;
                            }
                        }
                        if (filter.to) {
                            if (filter.to < val) {
                                return true;
                            }
                        }
                        return false;
                    }
                    case FilterType.NumberRange: {
                        if (filter.from) {
                            if (filter.from > item[key]) {
                                return true;
                            }
                        }
                        if (filter.to) {
                            if (filter.to < item[key]) {
                                return true;
                            }
                        }
                        return false;
                    }
                    case FilterType.MoneyRange: {
                        if (filter.from) {
                            if (filter.from > item[key]) {
                                return true;
                            }
                        }
                        if (filter.to) {
                            if (filter.to < item[key]) {
                                return true;
                            }
                        }
                        return false;
                    }
                    case FilterType.Search: {
                        let val = item[key];
                        const filterOption = props.values[key].filter;
                        if (filterOption.type === FilterType.Search && filterOption.toStr) {
                            val = filterOption.toStr(item[key]);
                        }
                        if (filter.query) {
                            const cmp = stringCompare(val, filter.query);
                            return cmp.score > 1;
                        }
                        return false;
                    }
                    case FilterType.Category: {
                        if (filter.categories.length == 0) {
                            return false;
                        }
                        const selected = filter.categories;
                        const filterOption = props.values[key].filter;
                        const value = item[key];
                        const categories = filterOption.type === FilterType.Category &&
                            filterOption.toCategories
                            ? filterOption.toCategories(value)
                            : Array.isArray(value)
                                ? value
                                : [value];
                        return !categories.some((one) => selected.includes(one));
                    }
                    case FilterType.Boolean: {
                        let val = item[key];
                        const filterOption = props.values[key].filter;
                        if (filterOption.type === FilterType.Boolean &&
                            filterOption.toBoolean) {
                            val = filterOption.toBoolean(item[key]);
                        }
                        return filter.showTrue == null ? true : filter.showTrue ? !val : val;
                    }
                }
            });
            // True means keep
            if (props.customFilter) {
                if (!props.customFilter(item)) {
                    return false;
                }
            }
            return invalid == null;
        });
    }, [appliedFilters, list]);
    const resultingList = useMemo(() => {
        if (props.searchable) {
            return search(filteredList, props.toSearchStr, searchQuery, {
                returnMeta: false,
            });
        }
        return filteredList;
    }, [
        props.searchable,
        searchQuery,
        'toSearchString' in props ? props.toSearchString : undefined,
        filteredList,
    ]);
    const values = useMemo(() => fromEntries(entries(props.values).map(([key, val]) => {
        return [
            key,
            {
                ...val,
                filterActive: val?.filter
                    ? (filter) => {
                        const f = filter[key];
                        switch (f.type) {
                            case FilterType.Category: {
                                return f.categories.length != 0;
                            }
                            case FilterType.Boolean: {
                                return f.showTrue != null;
                            }
                            case FilterType.DateRange: {
                                return f.from != null || f.to != null;
                            }
                            case FilterType.MoneyRange: {
                                return f.from != null || f.to != null;
                            }
                            case FilterType.NumberRange: {
                                return f.from != null || f.to != null;
                            }
                            case FilterType.Search: {
                                return f.query != null;
                            }
                        }
                        assertUnreachable(f, 'Unsupported filter type');
                    }
                    : undefined,
                filter: val?.filter != null
                    ? (props) => {
                        switch (val.filter.type) {
                            case FilterType.DateRange: {
                                const applied = props.filter[key];
                                if (applied?.type !== FilterType.DateRange) {
                                    return;
                                }
                                return (React.createElement(filters.DateRangeFilter, { onChange: (from, to) => {
                                        props.setFilter({
                                            ...props.filter,
                                            [key]: { ...props.filter[key], from, to },
                                        });
                                    }, from: applied.from, to: applied.to }));
                            }
                            case FilterType.NumberRange: {
                                const applied = props.filter[key];
                                if (applied?.type !== FilterType.NumberRange) {
                                    return;
                                }
                                return (React.createElement(filters.NumberRangeFilter, { onChange: (from, to) => {
                                        props.setFilter({
                                            ...props.filter,
                                            [key]: { ...props.filter[key], from, to },
                                        });
                                    }, from: applied.from, to: applied.to }));
                            }
                            case FilterType.MoneyRange: {
                                const applied = props.filter[key];
                                if (applied?.type !== FilterType.MoneyRange) {
                                    return;
                                }
                                return (React.createElement(filters.MoneyRangeFilter, { onChange: (from, to) => {
                                        props.setFilter({
                                            ...props.filter,
                                            [key]: { ...props.filter[key], from, to },
                                        });
                                    }, from: applied.from, to: applied.to }));
                            }
                            case FilterType.Search: {
                                const applied = props.filter[key];
                                if (applied?.type !== FilterType.Search) {
                                    return;
                                }
                                return (React.createElement(filters.SearchFilter, { onChange: (query) => {
                                        props.setFilter({
                                            ...props.filter,
                                            [key]: { ...props.filter[key], query },
                                        });
                                    }, query: applied.query }));
                            }
                            case FilterType.Category: {
                                const applied = props.filter[key];
                                if (applied?.type !== FilterType.Category) {
                                    return;
                                }
                                return (React.createElement(filters.CategoryFilter, { onChange: (selected) => {
                                        props.setFilter({
                                            ...props.filter,
                                            [key]: {
                                                ...props.filter[key],
                                                categories: selected,
                                            },
                                        });
                                    }, categories: val.filter.categories, selected: applied.categories }));
                            }
                            case FilterType.Boolean: {
                                const applied = props.filter[key];
                                if (applied?.type !== FilterType.Boolean) {
                                    return;
                                }
                                return (React.createElement(filters.BooleanFilter, { onChange: ({ showTrue, }) => {
                                        props.setFilter({
                                            ...props.filter,
                                            [key]: {
                                                ...props.filter[key],
                                                showTrue,
                                            },
                                        });
                                    }, filter: applied }));
                            }
                        }
                    }
                    : undefined,
            },
        ];
    })), [props.values]);
    const showing = useMemo(() => {
        if (props.pagination.type === 'none') {
            return resultingList;
        }
        return resultingList.slice(props.pagination.type == 'infinite'
            ? 0
            : page * props.pagination.pageSize, (page + 1) * props.pagination.pageSize);
    }, [resultingList, page, props.pagination]);
    useEffect(() => {
        const maxPage = getMaxPage(resultingList.length, props.pagination.pageSize);
        if (page < 0) {
            setPage(0);
        }
        else if (page > maxPage) {
            setPage(Math.max(0, maxPage));
        }
    }, [appliedFilters, list]);
    return (React.createElement(React.Fragment, null,
        props.pagination.type === 'pages' ? (React.createElement(PaginationWrapper, { top: true },
            React.createElement(PaginationButton, { onClick: () => {
                    setPage((p) => (p <= 0 ? 0 : p - 1));
                } },
                React.createElement(ArrowLeft, null)),
            React.createElement("span", { style: { fontSize: '0.85em' } },
                page + 1,
                " /",
                ' ',
                resultingList.length === 0
                    ? 1
                    : Math.ceil(resultingList.length / props.pagination.pageSize)),
            React.createElement(PaginationButton, { onClick: () => {
                    const maxPage = getMaxPage(resultingList.length, props.pagination.pageSize);
                    setPage((p) => (p >= maxPage ? p : p + 1));
                } },
                React.createElement(ArrowRight, null)))) : null,
        props.selectable ? (React.createElement(View, { data: showing, genKey: props.genKey, values: values, filter: appliedFilters, setFilter: setAppliedFilters, direction: direction, updateSortDirection: setDirection, sortKey: sortKey, updateSortKey: setSortKey, selectable: props.selectable, selected: props.selected, onSelect: props.onSelect, selectKey: props.selectKey, loading: props.loading, onClick: props.onClick, searchable: props.searchable, searchFocused: 'searchFocused' in props ? props.searchFocused : undefined, searchQuery: searchQuery, empty: page === 0 && showing.length === 0, dataLoaded: true, setSearchQuery: setSearchQuery })) : (React.createElement(View, { data: showing, genKey: props.genKey, values: values, filter: appliedFilters, setFilter: setAppliedFilters, direction: direction, updateSortDirection: setDirection, sortKey: sortKey, updateSortKey: setSortKey, loading: props.loading, onClick: props.onClick, searchable: props.searchable, searchFocused: 'searchFocused' in props ? props.searchFocused : undefined, searchQuery: searchQuery, empty: page === 0 && showing.length === 0, dataLoaded: true, setSearchQuery: setSearchQuery })),
        props.pagination.type === 'pages' ? (React.createElement(PaginationWrapper, { top: false },
            React.createElement(PaginationButton, { onClick: () => {
                    setPage((p) => (p <= 0 ? 0 : p - 1));
                } },
                React.createElement(ArrowLeft, null)),
            React.createElement("span", { style: { fontSize: '0.8em' } },
                page + 1,
                " /",
                ' ',
                resultingList.length === 0
                    ? 1
                    : Math.ceil(resultingList.length / props.pagination.pageSize)),
            React.createElement(PaginationButton, { onClick: () => {
                    const maxPage = getMaxPage(resultingList.length, props.pagination.pageSize);
                    setPage((p) => (p >= maxPage ? p : p + 1));
                } },
                React.createElement(ArrowRight, null)))) : props.pagination.type === 'infinite' ? (React.createElement(Pagination, { type: props.pagination.type },
            React.createElement(React.Fragment, null,
                React.createElement("small", null,
                    lang.table.showing,
                    " ",
                    showing.length,
                    " ",
                    lang.table.outOf,
                    ' ',
                    resultingList.length,
                    ' '),
                showing.length < resultingList.length ? (React.createElement("button", { onClick: () => {
                        setPage((p) => p + 1);
                    } }, lang.table.loadMore)) : null))) : null));
};
const getMaxPage = (items, pageSize) => {
    const page = items / pageSize;
    const lastPage = Number.isInteger(page) ? page - 1 : Math.floor(page);
    return lastPage;
};
const Pagination = styled.div `
  padding: 1em;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5em;
  flex-direction: ${(p) => (p.type === 'pages' ? 'row' : 'column')};
`;
const PaginationWrapper = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid
    ${(props) => (props.top ? THEME.BORDER_STRONG : THEME.BORDER)};
  color: ${THEME.COLOR_PRIMARY};
`;
const PaginationButton = styled.button `
  background: none;
  color: inherit;
  border: none;
  padding: 0.5em;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`;
export default PowerTable;
//# sourceMappingURL=PowerTable.js.map